package com.example.positionahead.domain

import com.example.positionahead.model.*
import kotlin.math.abs

object PortfolioHealthAnalyzer {

    fun analyze(summary: PortfolioSummary, goal: FinancialGoal): PortfolioHealth {
        val highlights = mutableListOf<HealthReason>()
        var score = 100

        // 1. Diversification
        if (summary.positions.size < 5) {
            score -= 15
            highlights.add(HealthReason("Low Diversification", "You only own ${summary.positions.size} positions. Aim for 8-12 to reduce individual stock risk.", false))
        } else if (summary.positions.size > 20) {
            score -= 5
            highlights.add(HealthReason("Over Diversified", "Owning ${summary.positions.size} positions may lead to 'diworsification'. Focus on your best ideas.", false))
        } else {
            highlights.add(HealthReason("Good Diversification", "Holding ${summary.positions.size} positions is a healthy mix for your profile.", true))
        }

        // 2. Allocation Drift
        val stockTarget = goal.effectiveStockTarget().toDouble()
        val stockDrift = abs(summary.stockAllocationPercent - stockTarget)
        if (stockDrift > goal.rebalanceThresholdPercent) {
            score -= 10
            highlights.add(HealthReason("Allocation Drift", "Equity exposure is off target by ${stockDrift.toInt()}%. Rebalancing may be needed.", false))
        } else {
            highlights.add(HealthReason("Balanced Allocation", "Your current stock/bond mix is aligned with your strategy.", true))
        }

        // 3. Concentration
        val heavyPositions = summary.positions.filter { it.portfolioWeightPercent > goal.maxSinglePositionPercent }
        if (heavyPositions.isNotEmpty()) {
            score -= 10
            highlights.add(HealthReason("Concentration Risk", "${heavyPositions.first().position.symbol} exceeds your ${goal.maxSinglePositionPercent}% limit.", false))
        }

        // 4. Yield
        if (goal.objective == PortfolioObjective.INCOME) {
            val yield = if (summary.totalMarketValue > 0) summary.estimatedAnnualIncome / summary.totalMarketValue * 100 else 0.0
            if (yield < goal.targetYieldPercent - 0.5) {
                score -= 10
                highlights.add(HealthReason("Yield Gap", "Your current yield (${yield.toInt()}%) is below your ${goal.targetYieldPercent}% target.", false))
            }
        }

        return PortfolioHealth(
            score = score.coerceIn(0, 100),
            highlights = highlights,
            gaps = identifyGaps(summary)
        )
    }

    private fun identifyGaps(summary: PortfolioSummary): List<PortfolioGap> {
        val gaps = mutableListOf<PortfolioGap>()
        val symbols = summary.positions.map { it.position.symbol.uppercase() }.toSet()
        
        // International
        if (!symbols.contains("VXUS") && !symbols.contains("VEU")) {
            gaps.add(PortfolioGap("International", "Low exposure", "Adding global equities reduces US-specific economic risk.", listOf("VXUS", "VEU")))
        } else {
            gaps.add(PortfolioGap("International", "Strong exposure", "You have good global coverage."))
        }

        // Small Cap
        if (!symbols.contains("VB") && !symbols.contains("IWM")) {
            gaps.add(PortfolioGap("Small-cap", "Very low exposure", "Small caps can offer higher growth potential but more volatility.", listOf("VB", "IJR")))
        }

        // Dividends
        if (summary.estimatedAnnualIncome < (summary.totalMarketValue * 0.01)) {
            gaps.add(PortfolioGap("Dividend income", "Low exposure", "Consider adding yield-focused ETFs to boost recurring cash flow.", listOf("SCHD", "VYM")))
        }

        return gaps
    }
}
